import type { auth as authContracts, members as memberContracts } from '@personal-blog/contracts';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { uploadLibraryFile, type LibraryUploadProgress } from '@/features/files/api-uploads';
import { ownedFileContentUrl } from '@/features/files/utils/file-url';
import { apiFetch, type ApiRequestError } from '@/lib/api';

export interface MembersListFilter {
  q?: string;
  role?: memberContracts.RoleId;
  includeBanned?: boolean;
  limit?: number;
  offset?: number;
}

export interface MembersListResponse {
  members: memberContracts.Member[];
  total: number;
  limit: number;
  offset: number;
}

export interface MemberResponse {
  member: memberContracts.Member;
}

export type MemberHomepageResponse = memberContracts.MemberHomepageResponse;
export type MemberHomepageProfile = memberContracts.MemberHomepageProfile;
export type UpdateMemberHomepageProfileRequest =
  memberContracts.UpdateMemberHomepageProfileRequest;

export interface UploadedMemberAvatarImage {
  file: Awaited<ReturnType<typeof uploadLibraryFile>>;
  previewUrl: string;
}

export interface ParentChildBindingResponse {
  binding: memberContracts.ParentChildBinding;
}

export interface OkResponse {
  ok: true;
}

export interface AssistMemberVerificationResponse {
  ok: true;
  challenge: authContracts.VerificationChallenge | null;
}

export type ApproveMemberVerificationResponse = MemberResponse;

export const membersKeys = {
  all: ['members'] as const,
  list: (filter: MembersListFilter = {}) =>
    [
      'members',
      'list',
      Object.entries(filter)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => a.localeCompare(b)),
    ] as const,
  detail: (id: string) => ['members', 'detail', id] as const,
  homepage: (id: string, locale: 'zh' | 'en') => ['members', 'homepage', id, locale] as const,
  childPasswordResets: () => ['members', 'children', 'password-reset-requests'] as const,
};

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    usp.set(key, String(value));
  }
  const serialized = usp.toString();
  return serialized.length === 0 ? '' : `?${serialized}`;
}

function invalidateMembers(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: membersKeys.all });
}

const MEMBER_HOMEPAGE_STALE_MS = 10 * 60 * 1000;

export function useMembersList(
  filter: MembersListFilter = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<MembersListResponse, ApiRequestError> {
  return useQuery({
    queryKey: membersKeys.list(filter),
    enabled: options.enabled ?? true,
    queryFn: () =>
      apiFetch<MembersListResponse>(
        `/api/members${qs({
          q: filter.q,
          role: filter.role,
          includeBanned: filter.includeBanned,
          limit: filter.limit,
          offset: filter.offset,
        })}`,
      ),
    retry: false,
  });
}

export function useMemberDetail(
  id: string | undefined,
): UseQueryResult<MemberResponse, ApiRequestError> {
  return useQuery({
    queryKey: membersKeys.detail(id ?? ''),
    queryFn: () => apiFetch<MemberResponse>(`/api/members/${encodeURIComponent(id ?? '')}`),
    enabled: typeof id === 'string' && id.length > 0,
    retry: false,
  });
}

export function useMemberDetails(
  ids: readonly string[],
): UseQueryResult<MemberResponse, ApiRequestError>[] {
  const uniqueIds = Array.from(new Set(ids.filter((id) => id.length > 0)));
  return useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: membersKeys.detail(id),
      queryFn: () => apiFetch<MemberResponse>(`/api/members/${encodeURIComponent(id)}`),
      retry: false,
    })),
  });
}

export function useMemberHomepage(
  id: string | undefined,
  locale: 'zh' | 'en',
  options: { enabled?: boolean } = {},
): UseQueryResult<MemberHomepageResponse, ApiRequestError> {
  return useQuery({
    queryKey: membersKeys.homepage(id ?? '', locale),
    queryFn: () =>
      apiFetch<MemberHomepageResponse>(
        `/api/users/${encodeURIComponent(id ?? '')}/homepage${qs({ locale })}`,
      ),
    enabled: (options.enabled ?? true) && typeof id === 'string' && id.length > 0,
    retry: false,
    staleTime: MEMBER_HOMEPAGE_STALE_MS,
  });
}

export function useUpdateMemberHomepage(): UseMutationResult<
  { profile: memberContracts.MemberHomepageProfile },
  ApiRequestError,
  UpdateMemberHomepageProfileRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<{ profile: memberContracts.MemberHomepageProfile }>('/api/users/me/homepage', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      invalidateMembers(qc);
      void qc.invalidateQueries({ queryKey: ['members', 'homepage'] });
    },
  });
}

export async function uploadMemberAvatarImage(
  file: File,
  onProgress?: (progress: LibraryUploadProgress) => void,
): Promise<UploadedMemberAvatarImage> {
  const uploaded = await uploadLibraryFile(
    file,
    { partition: 'private', folderId: null },
    onProgress,
  );
  return {
    file: uploaded,
    previewUrl: ownedFileContentUrl(uploaded),
  };
}

export function useAssignMemberRole(): UseMutationResult<
  MemberResponse,
  ApiRequestError,
  { id: string; role: memberContracts.RoleId }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }) =>
      apiFetch<MemberResponse>(`/api/members/${encodeURIComponent(id)}/roles`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: (_data, vars) => {
      invalidateMembers(qc);
      void qc.invalidateQueries({ queryKey: membersKeys.detail(vars.id) });
    },
  });
}

export function useUpdateMemberAccount(): UseMutationResult<
  MemberResponse,
  ApiRequestError,
  { id: string; body: memberContracts.UpdateMemberAccountRequest }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) =>
      apiFetch<MemberResponse>(`/api/members/${encodeURIComponent(id)}/account`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, vars) => {
      invalidateMembers(qc);
      void qc.invalidateQueries({ queryKey: membersKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useAssistMemberVerification(): UseMutationResult<
  AssistMemberVerificationResponse,
  ApiRequestError,
  { id: string; kind: memberContracts.AssistMemberVerificationRequest['kind'] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, kind }) =>
      apiFetch<AssistMemberVerificationResponse>(
        `/api/members/${encodeURIComponent(id)}/verification/resend`,
        { method: 'POST', body: JSON.stringify({ kind }) },
      ),
    onSuccess: (_data, vars) => {
      invalidateMembers(qc);
      void qc.invalidateQueries({ queryKey: membersKeys.detail(vars.id) });
    },
  });
}

export function useApproveMemberVerification(): UseMutationResult<
  ApproveMemberVerificationResponse,
  ApiRequestError,
  { id: string; kind: memberContracts.ApproveMemberVerificationRequest['kind'] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, kind }) =>
      apiFetch<ApproveMemberVerificationResponse>(
        `/api/members/${encodeURIComponent(id)}/verification/approve`,
        { method: 'POST', body: JSON.stringify({ kind }) },
      ),
    onSuccess: (_data, vars) => {
      invalidateMembers(qc);
      void qc.invalidateQueries({ queryKey: membersKeys.detail(vars.id) });
    },
  });
}

export function useRevokeMemberRole(): UseMutationResult<
  MemberResponse,
  ApiRequestError,
  { id: string; role: memberContracts.RoleId }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }) =>
      apiFetch<MemberResponse>(
        `/api/members/${encodeURIComponent(id)}/roles/${encodeURIComponent(role)}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) => {
      invalidateMembers(qc);
      void qc.invalidateQueries({ queryKey: membersKeys.detail(vars.id) });
    },
  });
}

export function useBindParentChild(): UseMutationResult<
  ParentChildBindingResponse,
  ApiRequestError,
  { parentId: string; childId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ parentId, childId }) =>
      apiFetch<ParentChildBindingResponse>(
        `/api/members/${encodeURIComponent(parentId)}/children`,
        { method: 'POST', body: JSON.stringify({ childId }) },
      ),
    onSuccess: () => {
      invalidateMembers(qc);
    },
  });
}

export function useUnbindParentChild(): UseMutationResult<
  OkResponse,
  ApiRequestError,
  { parentId: string; childId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ parentId, childId }) =>
      apiFetch<OkResponse>(
        `/api/members/${encodeURIComponent(parentId)}/children/${encodeURIComponent(childId)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      invalidateMembers(qc);
    },
  });
}

export function useIssueInviteCode(): UseMutationResult<
  memberContracts.IssueInviteCodeResponse,
  ApiRequestError,
  { childId: string; ttlMinutes?: number }
> {
  return useMutation({
    mutationFn: ({ childId, ttlMinutes }) =>
      apiFetch<memberContracts.IssueInviteCodeResponse>('/api/members/invite-codes', {
        method: 'POST',
        body: JSON.stringify({
          childId,
          ...(ttlMinutes !== undefined ? { ttlMinutes } : {}),
        }),
      }),
  });
}

export function useRedeemInviteCode(): UseMutationResult<
  memberContracts.RedeemCodeResponse,
  ApiRequestError,
  { code: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<memberContracts.RedeemCodeResponse>('/api/members/redeem-code', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      invalidateMembers(qc);
    },
  });
}

export function useCreateChildAccount(): UseMutationResult<
  memberContracts.CreateChildAccountResponse,
  ApiRequestError,
  memberContracts.CreateChildAccountRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<memberContracts.CreateChildAccountResponse>('/api/members/children', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      invalidateMembers(qc);
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useChildPasswordResetRequests(): UseQueryResult<
  memberContracts.ChildPasswordResetListResponse,
  ApiRequestError
> {
  return useQuery({
    queryKey: membersKeys.childPasswordResets(),
    queryFn: () =>
      apiFetch<memberContracts.ChildPasswordResetListResponse>(
        '/api/members/children/password-reset-requests',
      ),
    retry: false,
  });
}

export function useCompleteChildPasswordReset(): UseMutationResult<
  OkResponse,
  ApiRequestError,
  { requestId: string; body: memberContracts.CompleteChildPasswordResetRequest }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, body }) =>
      apiFetch<OkResponse>(
        `/api/members/children/password-reset-requests/${encodeURIComponent(requestId)}/complete`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: membersKeys.childPasswordResets() });
      invalidateMembers(qc);
    },
  });
}

export function useBanMember(): UseMutationResult<
  MemberResponse,
  ApiRequestError,
  { id: string; reason?: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      apiFetch<MemberResponse>(`/api/members/${encodeURIComponent(id)}/ban`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason ?? null }),
      }),
    onSuccess: (_data, vars) => {
      invalidateMembers(qc);
      void qc.invalidateQueries({ queryKey: membersKeys.detail(vars.id) });
    },
  });
}

export function useUnbanMember(): UseMutationResult<
  MemberResponse,
  ApiRequestError,
  { id: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) =>
      apiFetch<MemberResponse>(`/api/members/${encodeURIComponent(id)}/unban`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: (_data, vars) => {
      invalidateMembers(qc);
      void qc.invalidateQueries({ queryKey: membersKeys.detail(vars.id) });
    },
  });
}
